import { usePerformFundTransferMutation } from 'admin/reduxQuery'
import { FundBalance } from 'admin/types'
import React, { useState } from 'react'
import ActionButton from 'uiComponents/buttons'
import { Col, Row } from 'uiComponents/flex'
import { LoaderWrapper } from 'uiComponents/loaders'
import Currency from 'uiComponents/money/currency'
import { ModalDialog, ModalDialogTitle } from 'uiComponents/popups/modal'
import { Body } from 'uiComponents/typography'
import { FormWrapper } from '../../uiComponents/form/form'
import TextAreaFieldFormik from '../../uiComponents/input/textInput/textAreaFieldFormik'
import { FundTransferFormValidationSchema } from './validationSchema'
import { t, Trans, useTranslation } from 'translations'

interface Props {
    balance?: FundBalance
    pendingBalance?: FundBalance
    accountSlug: string
    accountName: string
    amount: number
    children: ({ openModal }: { openModal: () => void }) => React.ReactElement
}

interface FundTransferText {
    title: string
    body: string | React.ReactElement
    action: string | React.ReactElement
}

interface GetFundTransferTextProps {
    accountName: string
    amount: number
    pendingAmount?: number
    currency: string
    willFundsBeLocked?: boolean
}

const getFundTransferText = ({
    accountName,
    amount,
    currency,
    willFundsBeLocked,
}: GetFundTransferTextProps): FundTransferText => {
    if (willFundsBeLocked) {
        return {
            title: t('admin.fundTransfer.notification.suspensionTitle'),
            body: (
                <Trans
                    i18nKey="admin.fundTransfer.notification.suspensionBody"
                    values={{ accountName }}
                    components={{ b: <b />, amount: <Currency amount={amount || 0} currency={currency} /> }}
                />
            ),
            action: <>{t('admin.fundTransfer.notification.lockPayout')}</>,
        }
    }

    return {
        title: t('admin.fundTransfer.notification.transferTitle'),
        body: (
            <Trans
                i18nKey="admin.fundTransfer.notification.transferBody"
                values={{ accountName }}
                components={{ b: <b />, amount: <Currency amount={amount} currency={currency} /> }}
            />
        ),
        action: (
            <Trans
                i18nKey="admin.fundTransfer.notification.transferAction"
                components={{ amount: <Currency amount={amount} currency={currency} /> }}
            />
        ),
    }
}

const FundTransferNotification: React.FC<Props> = ({
    balance,
    pendingBalance,
    accountName,
    accountSlug,
    amount,
    children,
}) => {
    const { t } = useTranslation()
    const text = getFundTransferText({
        accountName,
        amount,
        currency: (balance?.currency || pendingBalance?.currency) as string,
        willFundsBeLocked: amount > (balance?.value || 0),
    })
    const [open, setOpen] = useState<boolean>(false)
    const [performFundTransfer, { isLoading }] = usePerformFundTransferMutation()

    const handleTransfer = (values: any) => {
        performFundTransfer({
            ...values,
            accountSlug,
        }).then(() => setOpen(false))
    }

    return (
        <>
            {open && (
                <ModalDialog>
                    <LoaderWrapper loading={isLoading}>
                        <FormWrapper
                            formId="fundTransferConfirmationForm"
                            initialValues={{
                                amount: {
                                    currency: (balance?.currency || pendingBalance?.currency) as string,
                                    value: Number.parseFloat(amount as any).toFixed(2) as any,
                                },
                                capturedPaymentReference: null,
                            }}
                            validationSchema={FundTransferFormValidationSchema}
                            onSubmit={handleTransfer}
                        >
                            <ModalDialogTitle>{text.title}</ModalDialogTitle>
                            <Body size={2} style={{ padding: '1em 0' }}>
                                {text.body}
                            </Body>
                            <TextAreaFieldFormik
                                name="capturedPaymentReference"
                                label={t('admin.fundTransfer.log.columns.description')}
                                style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                                aria-rowspan={4}
                            />
                            <Row gutter={16} align="flex-end" style={{ marginTop: '3em' }}>
                                <Col span="auto">
                                    <ActionButton secondary onClick={() => setOpen(false)}>
                                        {t('common.cancel')}
                                    </ActionButton>
                                </Col>
                                <Col span="auto">
                                    <ActionButton type="submit">{text.action}</ActionButton>
                                </Col>
                            </Row>
                        </FormWrapper>
                    </LoaderWrapper>
                </ModalDialog>
            )}
            {children({ openModal: () => setOpen(true) })}
        </>
    )
}

export default FundTransferNotification
